import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, UserCheck, RefreshCw, Mail } from 'lucide-react';
import SeoMeta from './SeoMeta';

const sections = [
  {
    icon: <Database className="w-5 h-5" />,
    title: "1. Information We Collect",
    items: [
      "Personal information you provide (e.g., name, email, account details)",
      "Usage data (pages visited, features used, device/browser info)",
      "Cookies and similar tracking technologies",
    ],
  },
  {
    icon: <Eye className="w-5 h-5" />,
    title: "2. How We Use Your Information",
    items: [
      "To provide and improve our services",
      "To communicate with you about updates, offers, or support",
      "To analyze usage and enhance user experience",
      "To comply with legal obligations",
    ],
  },
  {
    icon: <UserCheck className="w-5 h-5" />,
    title: "3. Data Sharing",
    items: [
      "We do not sell your personal information",
      "We may share data with trusted service providers for business operations",
      "We may disclose information if required by law",
    ],
  },
  {
    icon: <Shield className="w-5 h-5" />,
    title: "4. Your Rights",
    items: [
      "Access, update, or delete your personal information",
      "Opt out of marketing communications",
      "Request data portability",
    ],
  },
  {
    icon: <Lock className="w-5 h-5" />,
    title: "5. Security",
    text: "We use industry-standard security measures to protect your data, but no method is 100% secure.",
  },
  {
    icon: <RefreshCw className="w-5 h-5" />,
    title: "6. Changes to This Policy",
    text: "We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated date.",
  },
  {
    icon: <Mail className="w-5 h-5" />,
    title: "7. Contact Us",
    text: null,
    isContact: true,
  },
];

const PrivacyPolicy: React.FC = () => (
  <div className="min-h-screen bg-white text-slate-900">
    <SeoMeta title="Privacy Policy | IndianStockMarketGPT" description="How IndianStockMarketGPT collects, uses and protects your data, including cookies, analytics and advertising." url="https://indianstockmarketgpt.com/privacy" />
    {/* Header */}
    <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 bg-slate-50 border-b border-slate-100">
      <div className="max-w-4xl mx-auto text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 border border-navy-100 bg-navy-50 rounded-full px-4 py-1.5 mb-8">
          <Lock className="w-3.5 h-3.5 text-navy-500" />
          <span className="text-xs font-medium text-navy-600 tracking-wide uppercase">Your Data, Your Rights</span>
        </div>

        <h1 className="text-4xl md:text-5xl font-bold font-serif mb-6 text-slate-900 leading-tight">
          Privacy <span className="text-navy-500">Policy</span>
        </h1>

        <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
          Last updated: July 16, 2025 &nbsp;·&nbsp; We value your privacy. This policy explains how
          Indian Stock Market GPT collects, uses, and protects your information.
        </p>
      </div>
    </section>

    {/* Policy sections */}
    <section className="pb-16 px-4 sm:px-6 lg:px-8 pt-10">
      <div className="max-w-4xl mx-auto space-y-6">
        {sections.map((section, index) => (
          <div
            key={index}
            className="bg-white border border-slate-200 rounded-xl p-7 hover:border-navy-200 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-navy-50 border border-navy-100 text-navy-500 flex-shrink-0">
                {section.icon}
              </div>
              <h2 className="text-lg font-semibold text-slate-800 font-serif">{section.title}</h2>
            </div>

            {section.items && (
              <ul className="space-y-2 pl-1">
                {section.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-navy-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            )}

            {section.text && (
              <p className="text-sm text-slate-600 leading-relaxed pl-1">{section.text}</p>
            )}

            {section.isContact && (
              <p className="text-sm text-slate-600 leading-relaxed pl-1">
                For any questions about this Privacy Policy, please visit our{' '}
                <Link to="/about" className="text-navy-500 hover:text-navy-700 underline underline-offset-2 transition-colors">
                  About Us
                </Link>{' '}
                page. There you will find a direct link to connect with the founder on LinkedIn.
              </p>
            )}
          </div>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-100">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-sm">
          <div className="w-12 h-12 rounded-xl bg-navy-50 border border-navy-100 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-6 h-6 text-navy-500" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-slate-900 mb-3">
            Your Privacy, Our Promise
          </h2>
          <p className="text-slate-500 mb-8 max-w-md mx-auto leading-relaxed">
            We are committed to protecting your data and empowering you with transparency and control.{' '}
            <span className="text-slate-700 font-medium">Thank you for trusting Indian Stock Market GPT.</span>
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-navy-500 hover:bg-navy-600 text-white font-semibold px-7 py-3 rounded-lg transition-colors text-sm"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default PrivacyPolicy;
